"use client";

import React, { useMemo } from "react";
import { useCompetitionHomeData } from "../../../context/CompetitionHomeDataContext";
import { TeamLogosScroll } from "../../../partials/teams/TeamLogosScroll";
import { MatchTabbedList } from "../../../partials/matches/MatchTabbedList";
import { SeasonStatsCards } from "../../../partials/stats/SeasonStatsCards";
import { PromoSlot } from "@/features/promo-slots/PromoSlot";
import { AdSlot } from "@/features/ads/AdSlot";
import { LeadersTables } from "../../../partials/players/LeadersTables";
import { getEmbedUrl } from "@/views/video/utils/getEmbedUrl";
import { SectionStrip, type BrandedStyle } from "@/components/layout/SectionStrip";
import { HOME_HERO, HOME_MATCHES, HOME_LEADERS, HOME_STATS } from "@/features/theme/sectionKeys";
import { useLabels } from "@/lib/labels/LabelsProvider";

interface FootballCompetitionHomeBetanoLayoutProps {
    videoShortsSlot?: React.ReactNode;
    videoHighlightsSlot?: React.ReactNode;
    sectionStyles?: Record<string, BrandedStyle>;
}

export function FootballCompetitionHomeBetanoLayout({
    videoShortsSlot,
    videoHighlightsSlot,
    sectionStyles = {},
}: FootballCompetitionHomeBetanoLayoutProps = {}) {
    const {
        promos,
        leaders,
        featuredPosts,
        allTeams,
        homeSlices,
        competitionStats,
        goalDistribution,
        hasCompetitionStats,
    } = useCompetitionHomeData();
    const homeLabels = useLabels().homePage;

    // Find the first featured post with a video embed for the hero section
    const heroVideo = useMemo(() => {
        const videoPost = featuredPosts?.find((post) => post.videoEmbed?.url);
        if (!videoPost?.videoEmbed) return null;
        const embedUrl = getEmbedUrl(videoPost.videoEmbed);
        if (!embedUrl) return null;
        return { embedUrl, title: videoPost.title, embedCode: videoPost.videoEmbed.embedCode };
    }, [featuredPosts]);

    return (
        <div className="min-h-screen bg-background">
            <main className="max-w-350 mx-auto px-4 pb-6 space-y-10">
                <SectionStrip brandedStyle={sectionStyles?.[HOME_HERO]}>
                    <div className="px-4 space-y-8">
                        {/* Ad: content-top */}
                        <AdSlot id="content-top" />

                        <div>
                            {/* Hero title */}
                            <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-3">
                                {homeLabels.featuredContent}
                            </h2>

                            {/* Hero Video from Featured Posts */}
                            {heroVideo && (
                                <div className="relative w-full aspect-video rounded-lg overflow-hidden shadow-md">
                                    {heroVideo.embedUrl ? (
                                        <iframe
                                            src={heroVideo.embedUrl}
                                            className="absolute inset-0 w-full h-full"
                                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                            allowFullScreen
                                            title={heroVideo.title}
                                        />
                                    ) : heroVideo.embedCode ? (
                                        <div
                                            className="absolute inset-0 w-full h-full [&>iframe]:w-full [&>iframe]:h-full"
                                            dangerouslySetInnerHTML={{ __html: heroVideo.embedCode }}
                                        />
                                    ) : null}
                                </div>
                            )}
                        </div>
                    </div>
                </SectionStrip>

                {/* Row 3: Latest Results and Upcoming Matches */}
                <SectionStrip brandedStyle={sectionStyles?.[HOME_MATCHES]}>
                    <MatchTabbedList
                        finishedMatches={homeSlices.lastFinished}
                        upcomingMatches={homeSlices.nextUpcoming}
                    />
                </SectionStrip>

                {/* Ad: mid-scroll-1 */}
                <AdSlot id="mid-scroll-1" />

                {/* Promo slot 0 - Giveaway section */}
                <PromoSlot row={promos[0] ?? null} />

                {/* Video Highlights */}
                {videoHighlightsSlot}

                {/* Video Shorts (Reels) */}
                {videoShortsSlot}

                {/* Promo slot 1 - Instagram CTA */}
                <PromoSlot row={promos[1] ?? null} />

                {/* Leaders (Top Scorers, Assisters, Goal Contributions) */}
                <SectionStrip brandedStyle={sectionStyles?.[HOME_LEADERS]}>
                    <LeadersTables leaders={leaders} />
                </SectionStrip>

                {/* Ad: mid-scroll-2 */}
                <AdSlot id="mid-scroll-2" />

                {/* Season Stats */}
                <SectionStrip brandedStyle={sectionStyles?.[HOME_STATS]}>
                    {hasCompetitionStats && (
                        <SeasonStatsCards
                            competitionStats={competitionStats}
                            goalDistribution={goalDistribution}
                            teamWithMostWins={homeSlices.teamWithMostWins}
                        />
                    )}
                </SectionStrip>

                {/* Promo slot 2 */}
                <PromoSlot row={promos[2] ?? null} />

                {/* Ad: content-bottom */}
                <AdSlot id="content-bottom" />
            </main>
        </div>
    );
}
